const DEFAULT_TOOLS = ['dashboard', 'profile', 'version', 'fleetdata'];

const EDIT_ALL_PERMISSION_BY_TOOL = {
  editor: 'global.edit_all_templates',
  publisher: 'global.edit_all_jobs',
  inspector: 'global.edit_all_jobs',
  report: 'global.edit_all_jobs',
};

const normalizeList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((item) => String(item).trim());
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeToolName = (tool) =>
  String(tool || '')
    .trim()
    .toLowerCase();

const extractPermissions = (value) =>
  normalizeList(value).filter((entry) => entry.includes('.'));

const isPowerAdmin = (user) => {
  if (!user || user.role !== 'admin') return false;
  const privil = parseInt(user.privil, 10);
  return !isNaN(privil) && privil === 1;
};

const hasRole = (user, expectedRoles = []) => {
  const allowedRoles = normalizeList(expectedRoles).map((role) =>
    role.toLowerCase()
  );

  if (allowedRoles.length < 1) return true;
  return allowedRoles.includes(String(user?.role || '').toLowerCase());
};

const getCurrentRole = (state) => {
  const currentUserRole = String(state.currentUser?.role || '').trim();
  if (!currentUserRole) return null;

  return (
    (state.userRoles || []).find(
      (role) =>
        String(role.rolename || '').toLowerCase() ===
        currentUserRole.toLowerCase()
    ) || null
  );
};

const getCurrentUserPermissions = (state) => {
  const currentPermissions = normalizeList(state.currentUserAllowedPermissions);
  if (currentPermissions.length > 0) {
    return Array.from(new Set(currentPermissions));
  }

  const role = getCurrentRole(state);
  const rolePermissions = extractPermissions(role?.permissions);
  const roleToolPermissions = extractPermissions(role?.toollist);
  const userPermissions = extractPermissions(state.currentUser?.permissions);
  const userToolPermissions = extractPermissions(state.currentUser?.toollist);

  return Array.from(
    new Set([
      ...rolePermissions,
      ...roleToolPermissions,
      ...userPermissions,
      ...userToolPermissions,
    ])
  );
};

const includesPermission = (permissions, permission) => {
  const permissionKey = String(permission || '').trim();
  if (!permissionKey) return false;

  return permissions.includes(permissionKey);
};

const isJobOwner = (user, job) => {
  if (!user || !job || !job.prodCreatedByUser) return false;
  return String(job.prodCreatedByUser._id || '') === String(user._id || '');
};

const getEditPermissionForTool = (tool) => {
  const normalizedTool = normalizeToolName(tool);
  if (!normalizedTool) return null;

  if (normalizedTool.includes('editor')) {
    return EDIT_ALL_PERMISSION_BY_TOOL.editor;
  }

  if (
    normalizedTool.includes('publisher') ||
    normalizedTool.includes('inspector') ||
    normalizedTool.includes('report')
  ) {
    return EDIT_ALL_PERMISSION_BY_TOOL.publisher;
  }

  return EDIT_ALL_PERMISSION_BY_TOOL[normalizedTool] || null;
};

const hasPermission = (state, permission) => {
  if (isPowerAdmin(state.currentUser)) return true;
  const permissions = getCurrentUserPermissions(state);
  return includesPermission(permissions, permission);
};

const hasAnyPermission = (state, permissions = []) => {
  if (isPowerAdmin(state.currentUser)) return true;
  const availablePermissions = getCurrentUserPermissions(state);

  return normalizeList(permissions).some((permission) =>
    includesPermission(availablePermissions, permission)
  );
};

const hasAllPermissions = (state, permissions = []) => {
  if (isPowerAdmin(state.currentUser)) return true;

  const requiredPermissions = normalizeList(permissions);
  if (requiredPermissions.length < 1) return true;

  const availablePermissions = getCurrentUserPermissions(state);
  return requiredPermissions.every((permission) =>
    includesPermission(availablePermissions, permission)
  );
};

const canAccessTool = (state, tool) => {
  const requestedTool = normalizeToolName(tool);
  if (!requestedTool) return false;

  if (DEFAULT_TOOLS.includes(requestedTool)) return true;

  const allowedTools = normalizeList(state.currentUserAllowedTools).map(
    normalizeToolName
  );

  return allowedTools.includes(requestedTool);
};

const canEditJob = (
  state,
  { job, tool, allowLegacyNoPermissions = false } = {}
) => {
  if (isPowerAdmin(state.currentUser)) return true;
  if (!job) return false;
  if (isJobOwner(state.currentUser, job)) return true;

  const permissions = getCurrentUserPermissions(state);
  if (allowLegacyNoPermissions && permissions.length === 0) return true;

  const editPermission = getEditPermissionForTool(tool);
  if (!editPermission) {
    console.warn(
      `[Auth] Unrecognized tool context: "${tool}". Denying access.`
    );
    return false;
  }

  return includesPermission(permissions, editPermission);
};

const canMutateTask = (
  state,
  {
    job,
    tool,
    isCompleted = false,
    isOffline = false,
    isReadOnly = false,
    allowLegacyNoPermissions = false,
  } = {}
) => {
  if (isCompleted || isOffline || isReadOnly) return false;

  return canEditJob(state, {
    job,
    tool,
    allowLegacyNoPermissions,
  });
};

export {
  DEFAULT_TOOLS,
  EDIT_ALL_PERMISSION_BY_TOOL,
  canAccessTool,
  canEditJob,
  canMutateTask,
  getEditPermissionForTool,
  getCurrentUserPermissions,
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
  hasRole,
  isJobOwner,
  isPowerAdmin,
  normalizeList,
};
